import { ErrorBoundary } from '@sentry/react';
import { ExperimentalShareModal } from '@statsbomb/kitbag-components';
import { useAtomValue, useSetAtom } from 'jotai';
import { ComponentProps, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { userConfigsAtom } from '@/atoms/queries/userConfigs';
import { toastDataAtom } from '@/atoms/toast';
import { accessListAtom } from '@/atoms/userConfigPermissions';
import { isShareUserConfigModalOpenAtom, resetUserConfigActionStateAtom, userConfigIdAtom } from '@/atoms/userConfigs';
import { useUpsertUserConfigPermissions } from '@/hooks/useUpsertUserConfigPermissions';
import { UserConfigWithTitle } from '@/types/userConfigs';
import { noop } from '@/utils/noop';
import { usePermissionsConfig } from './ShareUserConfigModal.hooks';

const ShareUserConfigModalBase = ({
  accessList,
  configName,
  isLoading,
  isError,
  onSubmit = noop,
  isPendingConfirm,
  isOwner,
}: {
  accessList?: ComponentProps<typeof ExperimentalShareModal>['users'];
  configName?: string;
  isLoading?: boolean;
  isError?: boolean;
  onSubmit?: ShareModalOnSubmit;
  isPendingConfirm?: boolean;
  isOwner?: boolean;
}) => {
  const { t } = useTranslation(['general', 'modal']);
  const resetUserConfigActionState = useSetAtom(resetUserConfigActionStateAtom);
  const permissionsConfig = usePermissionsConfig();

  const getTitle = () => {
    if (isError) return t('error');
    if (isLoading) return t('loading');
    return t('shareModal.title', { ns: 'modal', itemName: configName, interpolation: { escapeValue: false } });
  };

  const titleOwnerText = isOwner ? t('shareModal.titleOwnerText', { ns: 'modal' }) : '';

  return (
    <ExperimentalShareModal
      isOpen
      id="share-user-config-modal"
      title={getTitle()}
      onSubmit={onSubmit}
      users={accessList}
      onClose={resetUserConfigActionState}
      permissionsConfig={permissionsConfig}
      backLabel={t('back')}
      saveLabel={t('save')}
      shareLabel={t('share')}
      cancelLabel={t('cancel')}
      dismissLabel={t('dismiss')}
      accessListTitle={t('access')}
      ownerLabel={t('shareModal.ownerLabel', { ns: 'modal' })}
      titleOwnerText={titleOwnerText}
      dropdownLabel={t('shareModal.dropdown.label', { ns: 'modal' })}
      dropdownPlaceholder={t('shareModal.dropdown.placeholder', { ns: 'modal' })}
      dropdownGroupsLabel={t('shareModal.dropdown.groupsLabel', { ns: 'modal' })}
      dropdownUsersLabel={t('shareModal.dropdown.usersLabel', { ns: 'modal' })}
      dropdownNoOptionsLabel={t('shareModal.dropdown.noOptionsLabel', { ns: 'modal' })}
      dropdownClearIndicatorLabel={t('shareModal.dropdown.clearIndicatorLabel', { ns: 'modal' })}
      pendingChangesText={t('shareModal.pendingChangesText', { ns: 'modal' })}
      isPendingConfirm={isPendingConfirm}
    />
  );
};

type ShareModalOnSubmit = ComponentProps<typeof ExperimentalShareModal>['onSubmit'];

const ShareUserConfigModalWithData = () => {
  const accessList = useAtomValue(accessListAtom);
  const userConfigId = useAtomValue(userConfigIdAtom);
  const configs = useAtomValue(userConfigsAtom);
  const config = configs.data.find(({ configId }) => configId === userConfigId) as UserConfigWithTitle | undefined;
  const setToastData = useSetAtom(toastDataAtom);
  const resetUserConfigActionState = useSetAtom(resetUserConfigActionStateAtom);
  const { upsertUserConfigPermissions, isPending } = useUpsertUserConfigPermissions();

  if (!config) {
    throw new Error('Unable to find config to share');
  }

  const configName = config.definition.titleSettings.title;

  const onSubmit: ShareModalOnSubmit = async (_e, { permissions }) => {
    const { isSuccess } = await upsertUserConfigPermissions(permissions);
    setToastData({ toastName: 'shareConfig', type: isSuccess ? 'success' : 'error', entityName: configName });
    resetUserConfigActionState();
  };

  return (
    <ShareUserConfigModalBase
      accessList={accessList}
      configName={configName}
      onSubmit={onSubmit}
      isPendingConfirm={isPending}
      isOwner={config.permission === 'own'}
    />
  );
};

export const ShareUserConfigModal = () => {
  const isShareUserConfigModalOpen = useAtomValue(isShareUserConfigModalOpenAtom);

  return (
    isShareUserConfigModalOpen && (
      <ErrorBoundary fallback={<ShareUserConfigModalBase isError />}>
        <Suspense fallback={<ShareUserConfigModalBase isLoading />}>
          <ShareUserConfigModalWithData />
        </Suspense>
      </ErrorBoundary>
    )
  );
};
